import { useCurrentUser, useCapacity } from "@/services/hooks";
import { updateUser } from "@/services/api";
import { Box, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef, useState } from "react";

export default function AudienceCap() {
  const { data: currentUser } = useCurrentUser();
  const [cap, setCap] = useState("");
  const hasHydrated = useRef(false);

  useEffect(() => {
    if (currentUser && !hasHydrated.current) {
      setCap(currentUser.meta?.cap ?? "");
      hasHydrated.current = true;
    }
  }, [currentUser]);

  const { data: capacity, isLoading: isCapacityLoading } = useCapacity(
    currentUser?.key
  );
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      updateUser(null, {
        cap,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["current_user"], data);
      queryClient.invalidateQueries({
        queryKey: ["capacity", currentUser?.key],
      });
    },
  });

  const debouncedSave = useCallback(
    debounce((newCap) => {
      mutation.mutate();
    }, 1000),
    []
  );

  const handleCapChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      setCap("");
      debouncedSave("");
      return;
    }

    const numericString = inputValue.replace(/[^0-9]/g, "");
    if (numericString === "") {
      setCap("");
      debouncedSave("");
    } else {
      const numValue = Number(numericString);
      setCap(numValue);
      debouncedSave(numValue);
    }
  };

  const getHelperText = () => {
    if (isCapacityLoading || mutation.isPending) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={12} color="secondary" />
          <Typography variant="caption" color="text.secondary">
            {mutation.isPending ? "Saving..." : "Loading..."}
          </Typography>
        </Box>
      );
    }

    if (capacity?.count === undefined || capacity.count === null) {
      return (
        <Typography variant="caption" color="secondary">
          No cap. Unlimited fans can subscribe.
        </Typography>
      );
    }

    if (capacity.count <= 0) {
      return (
        <Typography variant="caption" color="warning">
          No spots left. Users will not be able to subscribe.
        </Typography>
      );
    }

    return (
      <Typography variant="caption" color="secondary">
        {capacity.count} more fans can subscribe.
      </Typography>
    );
  };

  return (
    <Stack spacing={4}>
      <TextField
        label="Paid Audience Cap"
        value={cap}
        onChange={handleCapChange}
        fullWidth
        helperText={getHelperText()}
        inputProps={{
          inputMode: "numeric",
          pattern: "[0-9]*",
        }}
        disabled={mutation.isPending}
      />
    </Stack>
  );
}
